import COUNTRY_FORMATS from '@/Constants/CountryFormats'
import XDatePicker from '@/Mantine/XDatePicker'
import XNumberInput from '@/Mantine/XNumberInput'
import XSelect from '@/Mantine/XSelect'
import XTextInput from '@/Mantine/XTextInput'
import { RootState } from '@/Store'
import { updateCurrentStep, updateLead } from '@/Store/leadFormSlice'
import { Button, Divider, HStack, TabItem, Tabs, VStack, XelaColor } from '@/XelaReact'
import { Checkbox, LoadingOverlay } from '@mantine/core'
import axios from 'axios'
import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const formattedCountries = Object.values(COUNTRY_FORMATS)

interface FormData {
  passport_number: string
  passport_nationality: string
  passport_issue_date: string
  passport_expiry_date: string
  is_visa_rejected_in_past: boolean
  visa_rejection_one_country: string
  visa_rejection_one_year: number | ''
  visa_rejection_one_reason: string
  visa_rejection_two_country: string
  visa_rejection_two_year: number | ''
  visa_rejection_two_reason: string
  visa_rejection_three_country: string
  visa_rejection_three_year: number | ''
  visa_rejection_three_reason: string
}

const initialData: FormData = {
  passport_number: '',
  passport_nationality: '',
  passport_issue_date: '',
  passport_expiry_date: '',
  is_visa_rejected_in_past: false,
  visa_rejection_one_country: '',
  visa_rejection_one_year: '',
  visa_rejection_one_reason: '',
  visa_rejection_two_country: '',
  visa_rejection_two_year: '',
  visa_rejection_two_reason: '',
  visa_rejection_three_country: '',
  visa_rejection_three_year: '',
  visa_rejection_three_reason: '',
}

const LeadFormVisaPassportDetail: FC<{ fields: string[]; requirements: string[] }> = ({ fields, requirements }) => {
  const dispatch = useDispatch()
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [formData, setFormData] = useState<FormData>(initialData)

  const tabItems: TabItem[] = []
  if (
    fields.includes('passport_number') ||
    fields.includes('passport_issue_date') ||
    fields.includes('passport_expiry_date') ||
    fields.includes('passport_nationality')
  ) {
    tabItems.push({ label: 'Passport', value: 'Passport' })
  }
  if (fields.includes('is_visa_rejected_in_past')) {
    tabItems.push({ label: 'Visa', value: 'Visa' })
  }
  const passportTab: string =
    fields.includes('passport_number') ||
    fields.includes('passport_issue_date') ||
    fields.includes('passport_expiry_date') ||
    fields.includes('passport_nationality')
      ? 'Passport'
      : fields.includes('is_visa_rejected_in_past')
      ? 'Visa'
      : ''
  const leadStore = useSelector((state: RootState) => state.leadForm)

  const [tab, setTab] = React.useState(passportTab)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (leadStore.lead) {
      const lead = leadStore.lead
      setFormData({
        passport_number: lead.passport_number || '',
        passport_nationality: lead.passport_nationality || '',
        passport_issue_date: lead.passport_issue_date || '',
        passport_expiry_date: lead.passport_expiry_date || '',
        is_visa_rejected_in_past: lead.is_visa_rejected_in_past || false,
        visa_rejection_one_country: lead.visa_rejections ? lead.visa_rejections[0]?.country || '' : '',
        visa_rejection_one_year: lead.visa_rejections
          ? lead.visa_rejections[0]?.year
            ? lead.visa_rejections[0].year
            : ''
          : '',
        visa_rejection_one_reason: lead.visa_rejections ? lead.visa_rejections[0]?.reason || '' : '',
        visa_rejection_two_country: lead.visa_rejections ? lead.visa_rejections[1]?.country || '' : '',
        visa_rejection_two_year: lead.visa_rejections
          ? lead.visa_rejections[1]?.year
            ? lead.visa_rejections[1].year
            : ''
          : '',
        visa_rejection_two_reason: lead.visa_rejections ? lead.visa_rejections[1]?.reason || '' : '',
        visa_rejection_three_country: lead.visa_rejections ? lead.visa_rejections[2]?.country || '' : '',
        visa_rejection_three_year: lead.visa_rejections
          ? lead.visa_rejections[2]?.year
            ? lead.visa_rejections[2].year
            : ''
          : '',
        visa_rejection_three_reason: lead.visa_rejections ? lead.visa_rejections[2]?.reason || '' : '',
      })
    }
  }, [leadStore.lead])

  const handleSubmit = async () => {
    if (leadStore.lead_id) {
      setLoading(true)
      try {
        const response = await axios.post(
          `/leads/form/${leadStore.lead_id}/visa-passport-details`,
          {
            ...formData,
            uuid: leadStore.lead_form_uuid,
            csrfToken: leadStore.csrfToken,
          }
        )

        const { action, payload } = response.data

        if (action === 'visa_passport_details:update') {
          if (payload.lead) {
            setTab(passportTab)
            dispatch(updateLead(payload.lead))
            dispatch(updateCurrentStep(leadStore.current_step + 1))
          }
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          setErrors(error.response.data.errors || {})
        }
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <>
      {leadStore.is_mobile_view && (
        <div style={{ position: 'fixed', left: 0, bottom: 0, width: '100vw', zIndex: 9999 }}>
          <HStack justifyContent="flex-end">
            <Button
              style={{
                borderRadius: 0,
                width: '100%',
                border: '1px solid #8E8C94 !important',
                background: '#FFFFFF',
                outline: 'none !important',
              }}
              label={'Back'}
              variant="secondary"
              onClick={() => {
                dispatch(updateCurrentStep(leadStore.current_step - 1))
              }}
            ></Button>
            <Button
              style={{
                borderRadius: 0,
                width: '100%',
                border: '1px solid #003CFF !important',
                outline: 'none !important',
              }}
              label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
              onClick={handleSubmit}
            ></Button>
          </HStack>
        </div>
      )}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ color: 'blueX' }} />
        <VStack spacing="12px" height="100%">
          <Tabs
            style={{
              width: '100%',
              borderBottomWidth: '2px',
              borderBottomStyle: 'solid',
              borderBottomColor: XelaColor.Gray12,
              borderRadius: '0px',
              flexWrap: leadStore.is_mobile_view ? 'wrap' : 'nowrap',
            }}
            items={tabItems}
            name="visa_passport_details_edit_modal_tabs"
            defaultValue={tab}
            onChangeHandle={(value) => {
              setTab(value)
            }}
          ></Tabs>
          {tab === 'Passport' && (
            <VStack
              spacing="12px"
              style={{
                flex: '1 1 auto',
                paddingRight: '10px',
                height: leadStore.is_mobile_view ? 'auto' : '0',
                marginBottom: leadStore.is_mobile_view ? '50px' : '0px',
                overflowY: 'auto',
              }}
            >
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                {fields.includes('passport_number') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '420px'}>
                    <XTextInput
                      required={requirements.includes('passport_number')}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.passport_number !== null}
                      data-autofocus
                      label={'Passport Number'}
                      error={errors.passport_number}
                      value={formData.passport_number}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          passport_number: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                {fields.includes('passport_nationality') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '420px'}>
                    <XTextInput
                      required={requirements.includes('passport_nationality')}
                      disabled={leadStore.lead_id !== null && leadStore?.lead?.passport_nationality !== null}
                      label={'Passport Nationality'}
                      error={errors.passport_nationality}
                      value={formData.passport_nationality}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          passport_nationality: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                )}
                <div style={{ width: '100%' }}></div>
              </HStack>
              <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                {fields.includes('passport_issue_date') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '420px'}>
                    <XDatePicker
                      required={requirements.includes('passport_issue_date')}
                      dateParser={(dateString) => {
                        return moment(dateString, 'DD/MM/YYYY').toDate()
                      }}
                      value={
                        formData.passport_issue_date
                          ? moment(formData.passport_issue_date as string, 'YYYY-MM-DD').toDate()
                          : null
                      }
                      width="100%"
                      placeholder="DD/MM/YYYY"
                      valueFormat={'DD/MM/YYYY'}
                      label={'Passport Issue Date'}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            passport_issue_date: moment(value).format('YYYY-MM-DD'),
                          }))
                        }
                      }}
                      error={errors.passport_issue_date}
                    />
                  </HStack>
                )}
                {fields.includes('passport_expiry_date') && (
                  <HStack width={leadStore.is_mobile_view ? '100%' : '420px'}>
                    <XDatePicker
                      required={requirements.includes('passport_expiry_date')}
                      dateParser={(dateString) => {
                        return moment(dateString, 'DD/MM/YYYY').toDate()
                      }}
                      value={
                        formData.passport_expiry_date
                          ? moment(formData.passport_expiry_date as string, 'YYYY-MM-DD').toDate()
                          : null
                      }
                      width="100%"
                      placeholder="DD/MM/YYYY"
                      valueFormat={'DD/MM/YYYY'}
                      label={'Passport Expiry Date'}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            passport_expiry_date: moment(value).format('YYYY-MM-DD'),
                          }))
                        }
                      }}
                      error={errors.passport_expiry_date}
                    />
                  </HStack>
                )}
                <div style={{ width: '100%' }}></div>
              </HStack>
            </VStack>
          )}
          {tab === 'Visa' && fields.includes('is_visa_rejected_in_past') && (
            <VStack
              spacing="12px"
              style={{
                flex: '1 1 auto',
                paddingRight: '10px',
                height: leadStore.is_mobile_view ? 'auto' : '0',
                marginBottom: leadStore.is_mobile_view ? '50px' : '0px',
                overflowY: 'auto',
              }}
            >
              <HStack>
                <HStack>
                  <Checkbox
                    required={requirements.includes('is_visa_rejected_in_past')}
                    label={'Is Visa Rejected in Past'}
                    color={'blueX'}
                    checked={formData.is_visa_rejected_in_past}
                    onChange={(event) => {
                      setFormData((prevData) => ({
                        ...prevData,
                        is_visa_rejected_in_past: event.target.checked,
                      }))
                    }}
                    error={errors.is_visa_rejected_in_past}
                  />
                </HStack>
              </HStack>
              <HStack>
                <Divider variant={'dotted'} color={XelaColor.Gray10}></Divider>
              </HStack>
              <VStack spacing="12px">
                <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      required={formData.is_visa_rejected_in_past}
                      disabled={!formData.is_visa_rejected_in_past}
                      searchable
                      data={formattedCountries}
                      label={'Rejection Country'}
                      value={formData.visa_rejection_one_country}
                      error={errors.visa_rejection_one_country}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            visa_rejection_one_country: value,
                          }))
                        }
                      }}
                    />
                  </HStack>
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XNumberInput
                      required={formData.is_visa_rejected_in_past}
                      disabled={!formData.is_visa_rejected_in_past}
                      type={'number'}
                      label={'Rejection Year'}
                      error={errors.visa_rejection_one_year}
                      defaultValue={formData.visa_rejection_one_year}
                      onChange={(value) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          visa_rejection_one_year: value,
                        }))
                      }}
                    />
                  </HStack>
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      required={formData.is_visa_rejected_in_past}
                      disabled={!formData.is_visa_rejected_in_past}
                      label={'Rejection Reason'}
                      error={errors.visa_rejection_one_reason}
                      value={formData.visa_rejection_one_reason}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          visa_rejection_one_reason: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                </HStack>
                <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      disabled={!formData.is_visa_rejected_in_past}
                      searchable
                      data={formattedCountries}
                      label={'Rejection Country'}
                      value={formData.visa_rejection_two_country}
                      error={errors.visa_rejection_two_country}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            visa_rejection_two_country: value,
                          }))
                        }
                      }}
                    />
                  </HStack>
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XNumberInput
                      disabled={!formData.is_visa_rejected_in_past}
                      type={'number'}
                      label={'Rejection Year'}
                      error={errors.visa_rejection_two_year}
                      defaultValue={formData.visa_rejection_two_year}
                      onChange={(value) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          visa_rejection_two_year: value,
                        }))
                      }}
                    />
                  </HStack>
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      disabled={!formData.is_visa_rejected_in_past}
                      label={'Rejection Reason'}
                      error={errors.visa_rejection_two_reason}
                      value={formData.visa_rejection_two_reason}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          visa_rejection_two_reason: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                </HStack>
                <HStack spacing="12px" style={{ flexWrap: 'wrap' }} alignItems="flex-start">
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XSelect
                      disabled={!formData.is_visa_rejected_in_past}
                      searchable
                      data={formattedCountries}
                      label={'Rejection Country'}
                      value={formData.visa_rejection_three_country}
                      error={errors.visa_rejection_three_country}
                      onChange={(value) => {
                        if (value) {
                          setFormData((prevData) => ({
                            ...prevData,
                            visa_rejection_three_country: value,
                          }))
                        }
                      }}
                    />
                  </HStack>
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XNumberInput
                      disabled={!formData.is_visa_rejected_in_past}
                      type={'number'}
                      label={'Rejection Year'}
                      error={errors.visa_rejection_three_year}
                      defaultValue={formData.visa_rejection_three_year}
                      onChange={(value) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          visa_rejection_three_year: value,
                        }))
                      }}
                    />
                  </HStack>
                  <HStack width={leadStore.is_mobile_view ? '100%' : '270px'}>
                    <XTextInput
                      disabled={!formData.is_visa_rejected_in_past}
                      label={'Rejection Reason'}
                      error={errors.visa_rejection_three_reason}
                      value={formData.visa_rejection_three_reason}
                      onChange={(event) => {
                        setFormData((prevData) => ({
                          ...prevData,
                          visa_rejection_three_reason: event.target.value,
                        }))
                      }}
                    />
                  </HStack>
                </HStack>
              </VStack>
            </VStack>
          )}
          {!leadStore.is_mobile_view && (
            <>
              <HStack>
                <Divider variant={'dotted'}></Divider>
              </HStack>
              <HStack spacing={'16px'} justifyContent="flex-end">
                <Button
                  label={'Back'}
                  variant="secondary"
                  onClick={() => {
                    dispatch(updateCurrentStep(leadStore.current_step - 1))
                  }}
                ></Button>
                <Button
                  label={leadStore.current_step === leadStore.total_step - 1 ? 'Save' : 'Save & Next'}
                  onClick={handleSubmit}
                ></Button>
              </HStack>
            </>
          )}
        </VStack>
      </div>
    </>
  )
}

export default LeadFormVisaPassportDetail
